import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { App } from "./App";
import { cssVariables, theme } from "./ui/theme";
// The kit's styles first, ours after, so where the two disagree ours win.
import "@mantine/core/styles.css";
import "./styles.css";

// The caching rules are in CLAUDE.md and docs/plan.md and they are decisions,
// not defaults.
//
// refetchOnWindowFocus is off because a list must not reorder under a rep
// mid-work. Somebody who alt-tabs to take a call has to come back to the row
// they left, not to a page that quietly rebuilt itself.
//
// staleTime is long because the lake changes on a ten-minute timer, not per
// second, and every screen shows when its data loaded and offers a refresh.
// Nothing persists between sessions, so signing out takes the cache with it.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("No #root element, so index.html is not what it should be.");

createRoot(root).render(
  <React.StrictMode>
    {/* Light only. docs/style-guide.md: no dark mode in this pass, and a
        kit that follows the system setting would hand a rep a dark screen
        nobody designed. */}
    <MantineProvider theme={theme} cssVariablesResolver={cssVariables} forceColorScheme="light">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  </React.StrictMode>,
);
